import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { setItemsTreeSetEditingGuard } from '@/common/queries/itemsTreeSetConsistency';

/**
 * Reports this table's cell-editor state into the tree-set reconciliation guard — ref-counted per
 * `queryClient`, not per table, so one opted-out table can't cancel another's reconciliation.
 *
 * Edge-triggered on open/close: a same-table cell switch moves `rowId` straight to another `rowId`
 * without passing through null, so it must not double-increment. The guard is only lowered on true
 * unmount, never on a `rowId`/`participates` change, so it isn't dropped mid-edit.
 */
export const useTreeSetEditingGuard = (rowId: number | null, participates: boolean): void => {
  const queryClient = useQueryClient();
  const wasEditingRef = React.useRef(false);
  const report = React.useCallback(
    (nextRowId: number | null) => {
      const isEditing = nextRowId !== null;
      if (isEditing === wasEditingRef.current) return;
      wasEditingRef.current = isEditing;
      setItemsTreeSetEditingGuard(queryClient, isEditing);
    },
    [queryClient],
  );

  const wasParticipatingRef = React.useRef(false);
  React.useEffect(() => {
    wasParticipatingRef.current = participates;
    report(participates ? rowId : null);
  }, [rowId, participates, report]);

  React.useEffect(
    () => () => {
      if (wasParticipatingRef.current) report(null);
    },
    [report],
  );
};
